/**
 * 워크로그 쿼리 키 팩토리.
 *
 * 이 키는 **생성처와 무효화처의 레이어가 다르다** — 키를 만드는 쪽은 `features/worklog` 의
 * 조회 훅이고, 무효화하는 쪽에는 `features/quest-task` 의 하루 마감(`useCloseDay`) 도 있다.
 * FSD상 feature 끼리는 서로 import 할 수 없으므로, 양쪽이 합법적으로 참조 가능한
 * entity 레이어에 키를 둔다. (리터럴을 양쪽에 복붙하면 한쪽만 어긋나도 타입 에러 없이
 * 조용히 stale 된다 — `queryKey` 가 `readonly unknown[]` 이라 오타를 타입이 못 잡는다.)
 * 같은 사유의 선례: `entities/quest/model/questKeys.ts`, `entities/checkpoint/model/checkpointKeys.ts`.
 *
 * 실제로 그 오타가 났었다: 하루 마감이 단수 `['workLog']` 를 무효화해 매칭 0건으로
 * 조용히 실패했고, 마감에서 쓴 회고가 새로고침 전까지 화면에 반영되지 않았다.
 * 조회 훅이 전부 `staleTime: 30_000` 이라 마운트 재조회로도 가려지지 않는다.
 *
 * 회귀 가드: `__tests__/workLogKeys.test.ts` · `__tests__/workLogInvalidation.test.ts`.
 */
import { defineInvalidation, defineQueryKeys } from '@/shared/lib/queryKeys';

/**
 * 팀 워크로그는 **의도적으로 다른 루트**다 — 남의 로그를 기간으로 조회하는 읽기 전용 뷰라
 * 내 워크로그 mutation 이 무효화 대상으로 삼지 않는다. 내 워크로그 무효화로 팀 뷰까지
 * 날리지 않도록 루트를 분리해 둔다.
 *
 * `defineQueryKeys` 는 팩토리 하나에 루트 하나를 강제하므로, 여기서는 **두 팩토리**로 나눈다.
 * 그래야 두 루트가 각자 타입 보호를 받는다 — 한 팩토리에 두 루트를 욱여넣으면 제약이
 * `'workLogs' | 'teamWorkLogs'` 로 느슨해져, 내 워크로그 키에 팀 루트를 적어도 통과한다.
 * 소비처가 쓰는 표면은 `workLogKeys.team` 그대로다.
 *
 * **이 루트에는 계약이 없다** — 무효화하는 곳이 0곳이라 선언할 것이 없다. 계약이 값을 가지려면
 * scope 가 둘 이상이고 뮤테이션마다 건드리는 게 달라야 하는데, 여기는 첫 조건부터 안 선다.
 */
const teamWorkLogKeys = defineQueryKeys('teamWorkLogs', (all) => ({
  range: (teamId: string | undefined, from: string, to: string) =>
    [...all, teamId, from, to] as const,
}));

const keys = defineQueryKeys('workLogs', (all) => ({
  /**
   * ── 무효화 전용 중간 접두사 ────────────────────────────────────────────
   *
   * 조회에 쓰지 않는다. 뮤테이션이 **무엇을 무효화하는지 선언**할 때 쓴다
   * (아래 `defineInvalidation` 계약).
   */
  listAll: [...all, 'list'] as const,
  todayAll: [...all, 'today'] as const,
  countAll: [...all, 'count'] as const,
  lifetimeStatsAll: [...all, 'lifetimeStats'] as const,

  /**
   * ── 조회 키 ────────────────────────────────────────────────────────────
   *
   * **세그먼트 순서를 바꿨다.** 예전에는 `byUser` 가 `['workLogs', userId]` 여서 나머지 셋
   * (`['workLogs', userId, 'today']` …)의 **접두사**였다. 그러면 "목록만 무효화" 가 곧
   * "그 사용자의 모든 뷰" 라서 계약을 표현할 수 없다(문서 §10-2 의 두 함정이 겹친 모양 —
   * 조회 키가 상위 접두사이면서 uuid 가 리터럴 자리를 차지했다).
   *
   * `userId` 는 **캐시 네임스페이스**다. 네 조회 함수(`fetchWorkLogs()` 등)는 인자를 받지
   * 않고 전부 "내 것" 을 부른다 — 계정이 바뀌었을 때 앞 계정의 응답을 읽지 않게 하는 용도라
   * 뒤 칸으로 보내도 의미가 그대로다.
   */
  byUser: (userId: string | undefined) => [...all, 'list', userId] as const,
  /** 지난 일지 이어 읽기(무한 조회) — `list` 범위라 메모 수정·일지 생성이 함께 무효화한다. */
  history: (userId: string | undefined) => [...all, 'list', userId, 'history'] as const,
  today: (userId: string | undefined) => [...all, 'today', userId] as const,
  /** 워크로그 **개수**(`workLog.count`) — 생성/삭제만 바꾼다. */
  count: (userId: string | undefined) => [...all, 'count', userId] as const,
  /** 평생 통계 — `SUBMITTED` 건수 + 그 일지들의 `revisionCount` 합. */
  lifetimeStats: (userId: string | undefined) => [...all, 'lifetimeStats', userId] as const,
}));

/**
 * 무효화 계약.
 *
 * - `list` — 워크로그 목록
 * - `today` — 오늘(KST) 일지
 * - `count` — 워크로그 **개수**. `workLog.count({ where: { userId } })` 라 **생성/삭제만** 바꾼다
 * - `lifetimeStats` — `SUBMITTED` 건수 + `revisionCount` 합. **제출**과, `SUBMITTED` 인 일지의
 *   **수정**(`revisionCount: { increment: 1 }`)만 바꾼다. 새 일지는 `DRAFT` 라 여기 안 잡힌다
 *
 * 8곳이 전부 광역이었는데 `count` 가 필요한 곳은 1곳, `lifetimeStats` 는 2곳뿐이다.
 *
 * `workLogRoot` 는 **배럴로 내보내지 않는다** — 열면 계약을 우회할 수 있다.
 */
const {
  keys: myWorkLogKeys,
  root: workLogRoot,
  invalidate: invalidateWorkLogScopes,
} = defineInvalidation(keys, {
  list: keys.listAll,
  today: keys.todayAll,
  count: keys.countAll,
  lifetimeStats: keys.lifetimeStatsAll,
});

export { invalidateWorkLogScopes, workLogRoot };

export const workLogKeys = {
  ...myWorkLogKeys,
  /** 팀 워크로그 — 루트가 `teamWorkLogs` 라 내 워크로그 무효화에 닿지 않는다(의도). */
  team: teamWorkLogKeys.range,
  /**
   * 팀 워크로그 무효화용 루트.
   *
   * 조회 키가 `(teamId, from, to)` 세 인자를 받아, 그 셋을 모르는 자리에서는 접두사를 만들
   * 수 없다. 알림 딥링크 착지(`app/config/deepLinkQueryScopes`)가 그런 자리다 —
   * WORKLOG_SUBMITTED 수신자는 팀장이고 착지 화면이 팀 일지인데, 어느 주를 보고 있는지는
   * 착지 시점에 알 수 없다. 위 도크스트링의 "무효화하는 곳이 0곳" 은 그 소비처가 생기면서
   * 깨졌다 — scope 가 하나뿐이라 계약(`defineInvalidation`)을 얹지는 않는다.
   */
  teamAll: teamWorkLogKeys.all,
};
